import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { FileDown, XCircle, RefreshCw, WifiOff, ShieldCheck } from 'lucide-react';
import { overlayClose } from '../lib/overlayClose';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NFCE_STATUS = {
  autorizado: { label: 'NFC-e autorizada', cls: 'bg-emerald-100 text-emerald-700' },
  contingencia_pendente: { label: 'Contingência (pendente)', cls: 'bg-amber-100 text-amber-800' },
  processando_autorizacao: { label: 'Processando', cls: 'bg-blue-100 text-blue-700' },
  erro_autorizacao: { label: 'NFC-e rejeitada', cls: 'bg-red-100 text-red-700' },
  denegado: { label: 'NFC-e denegada', cls: 'bg-red-100 text-red-700' },
  cancelado: { label: 'NFC-e cancelada', cls: 'bg-slate-200 text-slate-700' },
  draft_local: { label: 'NFC-e rascunho', cls: 'bg-purple-100 text-purple-700' },
};

export const NfceBadge = ({ nfce }) => {
  if (!nfce?.status) return null;
  const st = NFCE_STATUS[nfce.status] || { label: nfce.status, cls: 'bg-slate-100 text-slate-600' };
  return <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ml-1 ${st.cls}`} title={nfce.mensagem_sefaz || ''} data-testid="nfce-badge">{st.label}{nfce.number ? ` nº ${nfce.number}` : ''}</span>;
};

export const NfceActions = ({ sale, onChanged }) => {
  const [cancelOpen, setCancelOpen] = useState(false);
  const [just, setJust] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const nf = sale.nfce;
  if (!nf?.status) return null;
  const canCancel = nf.status === 'autorizado' || nf.status === 'draft_local';
  const issuedAt = new Date(nf.issued_at || nf.updated_at || nf.created_at || sale.created_at);
  const minutesLeft = Math.max(0, 30 - Math.floor((Date.now() - issuedAt.getTime()) / 60000));

  const doCancel = async () => {
    setBusy(true); setErr('');
    try {
      await axios.delete(`${API}/nfce/cancel/${sale.id}`, { data: { justificativa: just }, withCredentials: true });
      setCancelOpen(false); setJust(''); onChanged?.();
    } catch (e) { setErr(e.response?.data?.detail || 'Erro ao cancelar NFC-e'); } finally { setBusy(false); }
  };

  return (
    <span className="inline-flex items-center gap-2 ml-2">
      {nf.xml_path && <a href={`${API}/nfce/xml/${sale.id}`} className="text-slate-600 hover:text-blue-700 text-xs inline-flex items-center gap-0.5" title="Baixar XML autorizado" data-testid={`nfce-xml-${sale.id}`}><FileDown size={14} /> XML</a>}
      {canCancel && minutesLeft > 0 && (
        <button onClick={() => setCancelOpen(true)} className="text-red-600 hover:text-red-700 text-xs inline-flex items-center gap-0.5" title={`Cancelar NFC-e (restam ${minutesLeft} min)`} data-testid={`nfce-cancel-${sale.id}`}><XCircle size={14} /> Cancelar NFC-e</button>
      )}
      {cancelOpen && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" {...overlayClose(() => setCancelOpen(false))} data-testid="nfce-cancel-modal">
          <div className="bg-white rounded-md p-5 w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-900">Cancelar NFC-e nº {nf.number}</h3>
            <p className="text-xs text-slate-500 mb-3">Prazo legal: 30 min após a autorização (restam {minutesLeft} min). A justificativa (15–255 caracteres) é enviada à SEFAZ.</p>
            <textarea value={just} onChange={(e) => setJust(e.target.value)} rows={3} className="w-full border border-slate-300 rounded-md p-2 text-sm" placeholder="Ex.: Venda registrada em duplicidade pelo operador" data-testid="nfce-cancel-justificativa" />
            <p className="text-[11px] text-slate-400">{just.trim().length}/255</p>
            {err && <p className="text-sm text-red-600 mt-1" data-testid="nfce-cancel-error">{err}</p>}
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" onClick={() => setCancelOpen(false)}>Voltar</Button>
              <Button onClick={doCancel} disabled={busy || just.trim().length < 15} className="bg-red-600 hover:bg-red-700" data-testid="nfce-cancel-confirm">{busy ? 'Enviando à SEFAZ...' : 'Confirmar cancelamento'}</Button>
            </div>
          </div>
        </div>
      )}
    </span>
  );
};

// Faixa de contingência: NFC-e aguardando retransmissão (SEFAZ/Focus indisponível no momento da venda)
export const NfceContingencyBanner = ({ onChanged }) => {
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const load = () => axios.get(`${API}/nfce/pending`, { withCredentials: true }).then((r) => setPending(r.data.filter((p) => p.status === 'contingencia_pendente'))).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  if (pending.length === 0) return null;
  const retry = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await axios.post(`${API}/nfce/retry-pending`, {}, { withCredentials: true });
      setMsg(r.data.message || `${r.data.transmitted} transmitida(s), ${r.data.pending} pendente(s)`);
      load(); onChanged?.();
    } catch (e) { setMsg(e.response?.data?.detail || 'Falha na retransmissão'); } finally { setBusy(false); }
  };
  return (
    <div className="bg-amber-50 border-2 border-amber-300 rounded-md p-3 mb-4 flex flex-wrap items-center gap-3" data-testid="nfce-contingency-banner">
      <WifiOff size={20} className="text-amber-700" />
      <div className="flex-1 min-w-[220px]">
        <p className="font-bold text-amber-900">{pending.length} NFC-e emitida(s) em contingência offline aguardando a SEFAZ</p>
        <p className="text-xs text-amber-800">Vendas: {pending.map((p) => `#${p.sale_number}`).join(', ')} · Transmita até o fim do próximo dia útil.</p>
        {msg && <p className="text-xs font-medium text-amber-900 mt-1" data-testid="nfce-retry-msg">{msg}</p>}
      </div>
      <Button size="sm" onClick={retry} disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid="nfce-retry-btn"><RefreshCw size={14} className={`mr-1 ${busy ? 'animate-spin' : ''}`} /> Transmitir agora</Button>
    </div>
  );
};

export const NfceReadyPill = () => {
  const [r, setR] = useState(null);
  useEffect(() => { axios.get(`${API}/nfce/readiness`, { withCredentials: true }).then((x) => setR(x.data)).catch(() => {}); }, []);
  if (!r) return null;
  return r.mode === 'mock'
    ? <span className="text-[10px] px-2 py-0.5 rounded bg-purple-100 text-purple-700 font-bold" title="Configure o token Focus NFe em Configurações → Fiscal" data-testid="nfce-mode-pill">NFC-e em modo rascunho</span>
    : <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold inline-flex items-center gap-1" data-testid="nfce-mode-pill"><ShieldCheck size={10} /> NFC-e {r.ambiente}</span>;
};
